<template>
  <th>
    {{ userData.user_id }}
  </th>
  <td v-if="userData.type != 'customer'">
    <figure class="image is-64x64 pointer" @click="selectFile">
      <img class="is-rounded" :src="userImageURL" alt="click_to_upload">
    </figure>
    <input type="file" class="is-hidden" ref="imageInput" @change="uploadImage($t)">
  </td>

  <td>
    <div v-if="!editing">{{ userData.name }}</div>
    <input v-else type="text" v-model="editedName" class="input">
  </td>

  <td>
    <div v-if="!editing">{{ userData.email }}</div>
    <input v-else type="text" v-model="editedEmail" class="input">
  </td>

  <td v-if="userData.type != 'manager'">
    <div v-if="!editing">{{ userData.phone }}</div>
    <input v-else type="text" v-model="editedPhone" class="input">
  </td>

  <td v-if="userData.type == 'customer'">
    <div v-if="!editing">{{ userData.address }}</div>
    <input v-else type="text" v-model="editedAddress" class="input">
  </td>

  <td v-if="userData.type == 'vet'">
    <div v-if="!editing">{{ userData.specialty }}</div>
    <input v-else type="text" v-model="editedSpecialty" class="input">
  </td>

  <td v-if="userData.type == 'customer'">
    <div v-if="!editing">{{ userData.tax_id }}</div>
    <input v-else type="text" v-model="editedTaxId" class="input">
  </td>

  <td :class="{ 'inactive-status': userData.status === $t('UserData.TK_Inactive') }">
    <div>{{ userData.status }}</div>
  </td>

  <td :class="{ 'inactive-status': userData.status === $t('UserData.TK_Inactive') }">
    <div>{{ userData.status_date }}</div>
  </td>

  <td>
    <div class="is-flex is-justify-content-start" style="gap: 1em;">
      <div v-if="userData.type != 'customer'">
        <button v-if="!editing"
          class="button is-warning is-small"
          @click="toggleEditing"
          :disabled="userData.status === $t('UserData.TK_Inactive')"
        >
          {{ $t('General.Edit') }}
        </button>

        <button v-else
          class="button is-success is-small"
          @click="save($t)"
        >
          {{ $t('General.Save') }}
        </button>
      </div>

      <div v-if="!editing && userData.type != 'customer'">
        <button
          class="button is-danger is-small"
          @click="remove(userData, $t)"
          :disabled="userData.status === $t('UserData.TK_Inactive')"
        >
          {{ $t('General.Deactivate') }}
        </button>
      </div>

      <div v-if="userData.type == 'customer'">
        <button
          class="button is-success is-small"
          @click="parentShowCustomerData(userData)"
        >
          {{ $t('General.Details') }}
        </button>
      </div>
    </div>
  </td>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapActions, mapGetters } from "vuex";
import { Notyf } from "notyf";

const notyf = new Notyf();

export default defineComponent({
  name: "UserTableRow",

  props: {
    userData: { type: Object, required: true },
    parentShowCustomerData: { type: Function, required: true }
  },

  data() {
    return {
      editing: false,
      editedName: "",
      editedEmail: "",
      editedPhone: "",
      editedAddress: "",
      editedSpecialty: "",
      editedTaxId: "",
      initialEmail: "",
      imageInput: null as HTMLInputElement | null
    };
  },

  computed: {
    ...mapGetters(['getUserData']),

    userImageURL(): string {
      const random = Math.round(Math.random() * 99999);
      return (
        process.env.BASE_URL +
        `api/users/image/${this.userData.user_id}/?hash=${random}`
      );
    }
  },

  methods: {
    ...mapActions([
      "deleteUser",
      "checkEmailIsTaken",
      "changeName",
      "changeEmail",
      "changePhone",
      "changeSpecialty",
      "fetchUsers",
      "uploadUserImage"
    ]),

    toggleEditing() {
      this.editedName = this.userData.name;
      this.editedEmail = this.userData.email;
      this.editedPhone = this.userData.phone;
      this.editedAddress = this.userData.address;
      this.editedSpecialty = this.userData.specialty;
      this.editedTaxId = this.userData.tax_id;
      this.initialEmail = this.userData.email;

      this.editing = !this.editing;
    },

    async remove(userData: any, $t: any) {
      if (confirm($t("UserData.ConfirmDelete"))) {
        try {
          await this.deleteUser({
            user: userData,
            language: this.$i18n.locale,
            updateUserId: this.getUserData.id
          });

          notyf.success($t("UserData.NotyfUserRemoved"));
        } catch (e) {
          notyf.error($t("UserData.NotyfErrorRemoveUser"));
        }
      }
    },

    async save($t: any) {
      let emailTaken = false;

      if (this.editedEmail !== this.initialEmail) {
        emailTaken = await this.checkEmailIsTaken(this.editedEmail);
      }

      if (emailTaken) {
        notyf.error($t("CustomerData.NotyfEmailInUse"));
        return;
      }

      try {
        await this.changeName({
          name: this.editedName,
          user_id: this.userData.user_id
        });

        await this.changeEmail({
          email: this.editedEmail,
          user_id: this.userData.user_id
        });

        if (this.userData.type === "vet") {
          await this.changePhone({
            phone: this.editedPhone,
            user_id: this.userData.user_id,
            type: this.userData.type
          });

          await this.changeSpecialty({
            specialty: this.editedSpecialty,
            user_id: this.userData.user_id
          });
        }

        notyf.success($t("UserData.NotyfDataEdited"));
      } catch (e) {
        notyf.error($t("UserData.NotyfErrorUpdatingData"));
      }

      await this.fetchUsers({
        userType: this.userData.type,
        language: this.$i18n.locale,
        status: "All"
      });

      this.toggleEditing();
    },

    selectFile() {
      if (this.imageInput) {
        this.imageInput.click();
      } else {
        console.error("imageInput is null");
      }
    },

    async uploadImage($t: any) {
      try {
        if (this.imageInput?.files?.[0]) {
          await this.uploadUserImage({
            user_id: this.userData.user_id,
            file: this.imageInput.files[0]
          });

          this.fetchUsers({
            userType: this.userData.type,
            language: this.$i18n.locale,
            status: "All"
          });
        } else {
          notyf.error($t("UserData.NotyfErrorUploadingPhoto"));
        }
      } catch (e) {
        notyf.error($t("UserData.NotyfErrorUploadingPhoto"));
      }
    }
  },

  mounted() {
    this.initialEmail = this.userData.email;
    this.imageInput = this.$refs.imageInput as HTMLInputElement | null;
  }
});
</script>

<style scoped>
.pointer {
  cursor: pointer;
}

.inactive-status {
  color: #a00;
  font-weight: bold;
}
</style>
